import React, { useState, useEffect, useMemo } from "react";
import {
  Card,
  Row,
  Col,
  Form,
  Spinner,
  Button,
  Alert,
  Badge,
  Modal,
} from "react-bootstrap";
import api from "../api";
import { AuthContext } from "../context/AuthContext";
import { motion } from "framer-motion";
import { FaUserCircle, FaStar, FaMapMarkerAlt, FaCrown, FaCheckCircle } from "react-icons/fa";
import LocationSelect from "./LocationSelect";

export default function WorkerList() {
  const [workers, setWorkers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { user } = React.useContext(AuthContext);
  const [userCoords, setUserCoords] = useState(null);
  const [manageMessage, setManageMessage] = useState("");
  const [manageError, setManageError] = useState("");
  const [deletingId, setDeletingId] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingWorker, setEditingWorker] = useState(null);
  const createEmptyEditForm = () => ({
    category: "",
    experience: "",
    hourlyRate: "",
    description: "",
    state: "",
    city: "",
    isAvailable: true,
  });
  const [editForm, setEditForm] = useState(createEmptyEditForm);
  const [editError, setEditError] = useState("");
  const [editSubmitting, setEditSubmitting] = useState(false);

  const currentUserId = user?._id || user?.id || "";

  // Fetch worker categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await api.get("/workers/categories");
        if (res.data.success) {
          setCategories(res.data.categories);
        }
      } catch (err) {
        console.error("Failed to fetch categories:", err);
      }
    };
    fetchCategories();
  }, []);

  // Get user's coordinates from profile (if available), else reverse-geocode city/state
  useEffect(() => {
    const getCoords = async () => {
      if (user && user.city && user.state) {
        // If user has lat/lon in profile, use them (optional: extend profile to store lat/lon)
        if (user.lat && user.lon) {
          setUserCoords({ lat: user.lat, lon: user.lon });
          return;
        }
        // Otherwise, reverse-geocode city/state
        const url = `https://nominatim.openstreetmap.org/search?city=${encodeURIComponent(
          user.city
        )}&state=${encodeURIComponent(
          user.state
        )}&country=India&format=json&limit=1`;
        const res = await fetch(url);
        const data = await res.json();
        if (data && data[0]) {
          setUserCoords({
            lat: parseFloat(data[0].lat),
            lon: parseFloat(data[0].lon),
          });
        }
      }
    };
    getCoords();
  }, [user]);

  // Fetch workers
  const fetchWorkers = async ({ preserveFeedback = false } = {}) => {
    setLoading(true);
    setError("");
    if (!preserveFeedback) {
      setManageMessage("");
      setManageError("");
    }
    try {
      const params = new URLSearchParams();
      if (selectedCategory) params.append("category", selectedCategory);
      // If backend supports lat/lon & radius, use them:
      if (userCoords) {
        params.append("lat", userCoords.lat);
        params.append("lon", userCoords.lon);
        params.append("radius", 20); // 20km
      }
      const res = await api.get(`/workers?${params.toString()}`);
      if (res.data.success) {
        const normalizeForComparison = (value) =>
          typeof value === "string" ? value.trim().toLowerCase() : "";
        const userCity = normalizeForComparison(user?.city);
        const userState = normalizeForComparison(user?.state);
        const getDistanceKm = (worker) => {
          if (!userCoords || !worker?.lat || !worker?.lon) {
            return null;
          }
          const R = 6371;
          const dLat = ((worker.lat - userCoords.lat) * Math.PI) / 180;
          const dLon = ((worker.lon - userCoords.lon) * Math.PI) / 180;
          const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos((userCoords.lat * Math.PI) / 180) *
              Math.cos((worker.lat * Math.PI) / 180) *
              Math.sin(dLon / 2) *
              Math.sin(dLon / 2);
          const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
          return R * c;
        };
        const isPrimeWorker = (worker) =>
          Boolean(worker?.sellerPrime || worker?.userId?.isPrime);
        const isSameCityAsUser = (worker) => {
          if (!userCity) {
            return false;
          }
          const workerCity = normalizeForComparison(worker?.city);
          if (!workerCity || workerCity !== userCity) {
            return false;
          }
          if (!userState) {
            return true;
          }
          const workerState = normalizeForComparison(worker?.state);
          return !workerState || workerState === userState;
        };
        const getGroupPriority = (worker) => {
          const sameCity = isSameCityAsUser(worker);
          const prime = isPrimeWorker(worker);
          if (sameCity && prime) return 0;
          if (sameCity && !prime) return 1;
          if (!sameCity && prime) return 2;
          return 3;
        };
        // If backend doesn't filter by radius, filter here:
        let filtered = res.data.workers;
        if (userCoords) {
          filtered = filtered.filter((w) => {
            const distance = getDistanceKm(w);
            return distance === null || distance <= 20;
          });
        }
        const prioritized = [...filtered].sort((a, b) => {
          const aGroup = getGroupPriority(a);
          const bGroup = getGroupPriority(b);
          if (aGroup !== bGroup) {
            return aGroup - bGroup;
          }
          const aDistance = getDistanceKm(a);
          const bDistance = getDistanceKm(b);
          if (aDistance !== bDistance) {
            if (aDistance === null) return 1;
            if (bDistance === null) return -1;
            return aDistance - bDistance;
          }
          const aRating = Number(a?.rating || 0);
          const bRating = Number(b?.rating || 0);
          if (aRating !== bRating) {
            return bRating - aRating;
          }
          const aCreated = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
          const bCreated = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
          return bCreated - aCreated;
        });

        setWorkers(prioritized);
      } else {
        setWorkers([]);
        setError(res.data.message || "Failed to fetch workers.");
      }
    } catch (err) {
      setError("Failed to fetch workers. Please try again.");
      console.error("Failed to fetch workers:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userCoords) fetchWorkers();
    // eslint-disable-next-line
  }, [selectedCategory, userCoords]);

  const formatCategory = (value) =>
    value
      ? value
          .split("-")
          .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
          .join(" ")
      : "";

  const categoryOptions = useMemo(() => {
    const optionSet = new Set(categories);
    if (editingWorker?.category) {
      optionSet.add(editingWorker.category);
    }
    return Array.from(optionSet);
  }, [categories, editingWorker]);

  const closeEditModal = (force = false) => {
    if (editSubmitting && !force) {
      return;
    }

    setShowEditModal(false);
    setEditingWorker(null);
    setEditError("");
    setEditForm(createEmptyEditForm());
  };

  const openEditModal = (worker) => {
    if (!worker) {
      return;
    }

    setEditingWorker(worker);
    setEditForm({
      category: worker.category || "",
      experience:
        worker.experience !== undefined && worker.experience !== null
          ? String(worker.experience)
          : "",
      hourlyRate:
        worker.hourlyRate !== undefined && worker.hourlyRate !== null
          ? String(worker.hourlyRate)
          : "",
      description: worker.description || "",
      state: worker.state || "",
      city: worker.city || "",
      isAvailable:
        typeof worker.isAvailable === "boolean" ? worker.isAvailable : true,
    });
    setEditError("");
    setManageError("");
    setManageMessage("");
    setShowEditModal(true);
  };

  const handleEditFieldChange = (event) => {
    const { name, value } = event.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditAvailabilityChange = (event) => {
    setEditForm((prev) => ({ ...prev, isAvailable: event.target.checked }));
  };

  const handleEditSubmit = async (event) => {
    event.preventDefault();

    if (!editingWorker?._id) {
      setEditError("No worker listing selected to edit.");
      return;
    }

    if (!currentUserId) {
      setEditError("Please login to edit your worker listing.");
      return;
    }

    if (!editForm.category) {
      setEditError("Please select a category.");
      return;
    }

    const numericExperience = Number(editForm.experience);
    if (Number.isNaN(numericExperience) || numericExperience < 0) {
      setEditError("Experience must be a non-negative number.");
      return;
    }

    const numericHourlyRate = Number(editForm.hourlyRate);
    if (Number.isNaN(numericHourlyRate) || numericHourlyRate < 0) {
      setEditError("Hourly rate must be a non-negative number.");
      return;
    }

    const trimmedDescription = (editForm.description || "").trim();
    if (trimmedDescription.length < 50 || trimmedDescription.length > 500) {
      setEditError("Description must be between 50 and 500 characters.");
      return;
    }

    const trimmedState = (editForm.state || "").trim();
    const trimmedCity = (editForm.city || "").trim();
    if (!trimmedState || !trimmedCity) {
      setEditError("Please select both state and city.");
      return;
    }

    setEditError("");
    setManageError("");
    setEditSubmitting(true);

    try {
      const res = await api.patch(`/workers/${editingWorker._id}`, {
        userId: currentUserId,
        category: editForm.category,
        experience: numericExperience,
        hourlyRate: numericHourlyRate,
        description: trimmedDescription,
        state: trimmedState,
        city: trimmedCity,
        isAvailable: Boolean(editForm.isAvailable),
      });

      if (res.data?.success && res.data?.worker) {
        setWorkers((prev) =>
          prev.map((item) =>
            String(item._id) === String(editingWorker._id)
              ? res.data.worker
              : item
          )
        );
        setManageMessage(
          res.data?.message || "Worker listing updated successfully."
        );
        closeEditModal(true);
        await fetchWorkers({ preserveFeedback: true });
      } else {
        setEditError(res.data?.message || "Failed to update worker listing.");
      }
    } catch (err) {
      console.error("Worker listing update error:", err);
      setEditError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          "Failed to update worker listing."
      );
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleDeleteWorker = async (worker) => {
    if (!worker?._id) {
      return;
    }

    if (!currentUserId) {
      setManageError("Please login to manage your worker listings.");
      return;
    }

    const categoryLabel = formatCategory(worker.category) || "worker";
    const confirmed = window.confirm(`Remove your ${categoryLabel} listing?`);

    if (!confirmed) {
      return;
    }

    setManageError("");
    setManageMessage("");
    setEditError("");
    setDeletingId(String(worker._id));

    try {
      const res = await api.delete(`/workers/${worker._id}`, {
        data: { userId: currentUserId },
      });

      if (res.data?.success) {
        setWorkers((prev) =>
          prev.filter((item) => String(item._id) !== String(worker._id))
        );
        setManageMessage(
          res.data?.message || "Worker listing removed successfully."
        );
        if (editingWorker && String(editingWorker._id) === String(worker._id)) {
          closeEditModal(true);
        }
      } else {
        throw new Error(
          res.data?.message || "Failed to remove worker listing."
        );
      }
    } catch (err) {
      console.error("Worker listing delete error:", err);
      setManageError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          "Failed to remove worker listing."
      );
    } finally {
      setDeletingId("");
    }
  };

  return (
    <div className="worker-list container py-5">
      <div className="text-center mb-5">
        <h2 className="text-gradient fw-bold mb-2">Find Workers</h2>
        <p className="text-secondary">
          Discover skilled professionals in your area
        </p>
      </div>
      {/* Location Info Banner */}
      {user && user.city && user.state && (
        <div className="alert alert-info text-center mb-4 rounded-pill shadow-sm">
          Showing workers within 20km of{" "}
          <b>
            {user.city}, {user.state}
          </b>
        </div>
      )}
      <div className="filters mb-4">
        <Row className="justify-content-center">
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Category</Form.Label>
              <Form.Select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="rounded-pill shadow-sm"
              >
                <option value="">All Categories</option>
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {formatCategory(category)}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>
      </div>
      {error && (
        <div className="alert alert-danger text-center rounded-pill">
          {error}
        </div>
      )}
      {manageError && (
        <Alert variant="danger" className="text-center rounded-pill">
          {manageError}
        </Alert>
      )}
      {manageMessage && (
        <Alert variant="success" className="text-center rounded-pill">
          {manageMessage}
        </Alert>
      )}
      {loading ? (
        <div className="text-center my-5">
          <Spinner animation="border" />
        </div>
      ) : workers.length === 0 ? (
        <div className="text-center my-5">
          <FaUserCircle size={64} className="text-secondary mb-3" />
          <div className="fs-5 text-muted mb-2 fw-semibold">
            No workers found in your area.
          </div>
          <div className="text-secondary">
            Try changing the category filter.
          </div>
        </div>
      ) : (
        <Row className="g-4">
          {workers.map((worker, i) => {
            const primaryCategory =
              worker.category ||
              (Array.isArray(worker.categories) && worker.categories.length > 0
                ? worker.categories[0]
                : "");
            const ownerId =
              worker.userId &&
              (worker.userId._id || worker.userId.id || worker.userId);
            const isOwner =
              ownerId && currentUserId
                ? String(ownerId) === String(currentUserId)
                : false;
            const isPrimeSeller = Boolean(
              worker?.sellerPrime || (worker?.userId && worker.userId.isPrime)
            );

            return (
              <Col key={worker._id} md={6} lg={4}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    delay: 0.1 + i * 0.08,
                    duration: 0.5,
                    type: "spring",
                  }}
                  whileHover={{
                    scale: 1.03,
                    boxShadow: "0 8px 24px 0 rgba(37,99,235,0.13)",
                  }}
                >
                  <Card className="h-100 shadow-hover border-0 rounded-4 worker-card">
                    <Card.Body>
                      <div className="d-flex align-items-center mb-3">
                        <div className="me-3">
                          <div
                            className="bg-light rounded-circle d-flex align-items-center justify-content-center border border-2 border-primary"
                            style={{ width: 56, height: 56 }}
                          >
                            <span className="fw-bold fs-3 text-primary">
                              {worker.userId.username ? (
                                worker.userId.username[0].toUpperCase()
                              ) : (
                                <FaUserCircle size={36} />
                              )}
                            </span>
                          </div>
                        </div>
                        <div className="flex-grow-1">
                          <span className="fw-bold fs-5">
                            {worker.userId.username}
                          </span>
                          {isPrimeSeller && (
                            <Badge
                              bg="warning"
                              text="dark"
                              className="ms-2 rounded-pill d-inline-flex align-items-center gap-1"
                            >
                              <FaCrown /> Prime Pro
                            </Badge>
                          )}
                          {worker.verified && (
                            <Badge
                              bg="success"
                              className="ms-2 rounded-pill d-inline-flex align-items-center gap-1"
                              title="Skill verified by Rentalo"
                            >
                              <FaCheckCircle /> Verified
                            </Badge>
                          )}
                          {primaryCategory && (
                            <span className="badge bg-secondary text-capitalize rounded-pill px-3 py-1 mt-2">
                              {formatCategory(primaryCategory)}
                            </span>
                          )}
                          {typeof worker.isAvailable === "boolean" && (
                            <Badge
                              bg={worker.isAvailable ? "success" : "secondary"}
                              className="ms-2 text-uppercase"
                            >
                              {worker.isAvailable ? "Available" : "Unavailable"}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <Card.Subtitle className="mb-2 text-muted">
                        <span className="me-2">
                          <i className="bi bi-geo-alt-fill text-primary"></i>{" "}
                          {worker.city}, {worker.state}
                        </span>
                      </Card.Subtitle>
                      <Card.Text className="mb-2">
                        <span className="me-3">
                          <strong>Experience:</strong> {worker.experience} yrs
                        </span>
                        <span className="me-3">
                          <strong>Rate:</strong> ₹{worker.hourlyRate}/hr
                        </span>
                        <span className="me-3">
                          <strong>Rating:</strong>{" "}
                          <span className="badge bg-success rounded-pill">
                            <FaStar className="mb-1" />
                            {Number(worker.rating || 0).toFixed(1)}
                          </span>{" "}
                          <span className="text-muted small">
                            ({worker.totalRatings || 0})
                          </span>
                        </span>
                      </Card.Text>
                      <div className="mb-2">
                        <strong>Description:</strong>
                        <div
                          className="text-secondary small mt-1"
                          style={{ minHeight: 48 }}
                        >
                          {worker.description}
                        </div>
                      </div>
                      <div className="contact-info small mt-3 p-3 bg-light rounded-3 border">
                        <strong>Contact:</strong> <br />
                        <span className="d-block">
                          <i className="bi bi-telephone-fill text-primary"></i>{" "}
                          {worker.userId.phone}
                        </span>
                        <span className="d-block">
                          <i className="bi bi-envelope-fill text-primary"></i>{" "}
                          {worker.userId.email}
                        </span>
                      </div>
                      {isOwner && (
                        <div className="d-flex justify-content-between align-items-center mt-3">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => openEditModal(worker)}
                          >
                            Edit Listing
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            disabled={deletingId === String(worker._id)}
                            onClick={() => handleDeleteWorker(worker)}
                          >
                            {deletingId === String(worker._id) ? (
                              <span className="d-inline-flex align-items-center">
                                Removing
                                <Spinner
                                  animation="border"
                                  size="sm"
                                  className="ms-2"
                                  role="status"
                                />
                              </span>
                            ) : (
                              "Remove Listing"
                            )}
                          </Button>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </motion.div>
              </Col>
            );
          })}
        </Row>
      )}

      <Modal
        show={showEditModal}
        onHide={() => closeEditModal()}
        centered
        backdrop={editSubmitting ? "static" : true}
        keyboard={!editSubmitting}
      >
        <Form onSubmit={handleEditSubmit}>
          <Modal.Header closeButton={!editSubmitting}>
            <Modal.Title>Edit Worker Listing</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {editError && <Alert variant="danger">{editError}</Alert>}

            <Form.Group className="mb-3">
              <Form.Label>Category</Form.Label>
              <Form.Select
                name="category"
                value={editForm.category}
                onChange={handleEditFieldChange}
                required
              >
                <option value="">Choose a category</option>
                {categoryOptions.map((category) => (
                  <option key={category} value={category}>
                    {formatCategory(category)}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Years of Experience</Form.Label>
              <Form.Control
                type="number"
                name="experience"
                value={editForm.experience}
                min="0"
                onChange={handleEditFieldChange}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Hourly Rate (₹)</Form.Label>
              <Form.Control
                type="number"
                name="hourlyRate"
                value={editForm.hourlyRate}
                min="0"
                onChange={handleEditFieldChange}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={editForm.description}
                onChange={handleEditFieldChange}
                minLength={50}
                maxLength={500}
                required
              />
              <Form.Text className="text-muted">
                {editForm.description.length}/500 characters
              </Form.Text>
            </Form.Group>

            <div className="mb-3">
              <Form.Label className="fw-semibold">
                <FaMapMarkerAlt className="me-2 text-primary" /> Location
              </Form.Label>
              <div className="p-3 bg-light rounded-3 border">
                <LocationSelect
                  selectedState={editForm.state}
                  selectedCity={editForm.city}
                  onStateChange={(value) =>
                    setEditForm((prev) => ({ ...prev, state: value }))
                  }
                  onCityChange={(value) =>
                    setEditForm((prev) => ({ ...prev, city: value }))
                  }
                  stateLabel="State"
                  cityLabel="City"
                  stateRequired={true}
                  cityRequired={true}
                />
              </div>
            </div>

            <Form.Group className="mb-0">
              <Form.Check
                type="switch"
                id="worker-list-availability"
                label="Available for work"
                checked={Boolean(editForm.isAvailable)}
                onChange={handleEditAvailabilityChange}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => closeEditModal(true)}
              disabled={editSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={editSubmitting}>
              {editSubmitting ? "Saving..." : "Save Changes"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}
